// R11P GEOLOGICAL P0.5-P9E — session-scoped Investor GIS entitlement.
//
// The entitlement belongs to ONE identity. It is re-read whenever the signed-in
// user changes (login, logout, account switch, cross-tab auth signal — all of
// which flow through AuthContext.user), and a response is applied only if it
// was requested by the identity still signed in (reduceEntitlement). Nothing is
// cached outside React state and nothing is written to storage.

import { useCallback, useEffect, useReducer, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  initialSnapshot,
  reduceEntitlement,
  type EntitlementSnapshot,
} from '@/lib/investorGis';
import { investorGisService } from '@/services/investorGisService';

export function useInvestorGisEntitlement(): {
  snapshot: EntitlementSnapshot;
  reload: () => void;
  markAuthLost: () => void;
} {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [snap, dispatch] = useReducer(reduceEntitlement, userId, initialSnapshot);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    dispatch({ type: 'identity', userId });
    if (!userId) return undefined;
    let cancelled = false;
    void investorGisService.getEntitlement().then((result) => {
      if (!cancelled) dispatch({ type: 'response', userId, result });
    });
    return () => {
      cancelled = true;
    };
  }, [userId, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const markAuthLost = useCallback(() => dispatch({ type: 'auth_lost' }), []);

  // Between an identity change and the effect above, never render the
  // previous identity's snapshot — not even for one frame.
  const snapshot = snap.userId === userId ? snap : initialSnapshot(userId);
  return { snapshot, reload, markAuthLost };
}
